import { EditorIcon } from "@/components/environments/EditorIcon";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import { useListEditors, type PlainEditor } from "@/queries/editor-queries";
import {
    useGetOrganizationPolicies,
    useUpdateOrganizationPolicies,
    type PlainOrganizationPolicies,
} from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { EditorVersionPolicy } from "gitpod-next-api/gitpod/v1/organization_pb";
import { MultiSelect } from "@/components/podkit/forms/MultiSelect";
import { useFlagIdeMultiVersionEnabled } from "@/hooks/use-flag";

import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

type AvailableEditorsProps = {
    editable?: boolean;
};

export const AvailableEditors: FC<AvailableEditorsProps> = ({ editable = true }) => {
    const [isManageEditorsModalOpen, setIsManageEditorsModalOpen] = useState(false);

    const { data: editors, isLoading: isEditorsLoading } = useListEditors();
    const { data: organizationPolicies, isLoading: isOrganizationPoliciesLoading } = useGetOrganizationPolicies();
    const isLoading = isEditorsLoading || isOrganizationPoliciesLoading;

    const availableEditors = useMemo(() => {
        if (!!organizationPolicies?.allowedEditorIds && organizationPolicies?.allowedEditorIds.length === 0) {
            return editors?.editors || [];
        }
        return editors?.editors.filter((editor) => organizationPolicies?.allowedEditorIds.includes(editor.id)) || [];
    }, [editors, organizationPolicies]);

    return (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle px-5 py-4">
            <div className="flex items-center justify-between">
                <Heading3>Available editors</Heading3>
                <Button
                    data-testid="manage-editors-button"
                    disabled={!editable}
                    onClick={() => setIsManageEditorsModalOpen(true)}
                    variant="secondary"
                    size="sm"
                    data-tracking-id="manage-editors-available-editors-element"
                >
                    Manage
                </Button>
            </div>
            <SkeletonBlock ready={!isLoading && availableEditors.length > 0}>
                <div className="flex flex-wrap gap-2">
                    {availableEditors.map((editor, index) => (
                        <EditorPill
                            key={`${editor.id}-${index}`}
                            editor={editor}
                            isDefault={editor.id === organizationPolicies?.defaultEditorId}
                        />
                    ))}
                </div>
            </SkeletonBlock>
            {isManageEditorsModalOpen && organizationPolicies && (
                <ManageEditorsModal
                    data-testid="manage-editors-modal"
                    onClose={() => setIsManageEditorsModalOpen(false)}
                    editors={editors?.editors || []}
                    policies={organizationPolicies}
                    editable={editable}
                />
            )}
        </div>
    );
};

const EditorPill: FC<{ editor: PlainEditor; isDefault?: boolean }> = ({ editor, isDefault }) => {
    return (
        <div
            className={cn(
                "inline-flex h-9 select-none items-center gap-2 whitespace-nowrap rounded-xl border-0.5 bg-surface-primary p-2 px-4 pb-[6.5px] pt-[5.5px] text-base font-medium",
                isDefault ? "border-border-brand" : "border-border-base",
            )}
        >
            <EditorIcon editor={editor} size="base" />
            <span>{editor.name}</span>
        </div>
    );
};

type ModalProps = {
    onClose: () => void;
    editors: PlainEditor[];
    policies: PlainOrganizationPolicies;
    editable?: boolean;
};
const ManageEditorsModal: FC<ModalProps> = ({ onClose, editors, policies, editable = true }) => {
    const { value: ideMultiVersionEnabled, loading: ideMultiVersionLoading } = useFlagIdeMultiVersionEnabled();
    const [selectedEditors, setSelectedEditors] = useState<Record<string, boolean>>(() => {
        if (policies.allowedEditorIds.length > 0) {
            return policies.allowedEditorIds.reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<string, boolean>,
            );
        }
        return editors.reduce(
            (acc, editor) => {
                acc[editor.id] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );
    });

    const [defaultEditor, setDefaultEditor] = useState<string>(policies.defaultEditorId || "invalid");

    // State for version restrictions per editor
    const [editorVersionRestrictions, setEditorVersionRestrictions] = useState<Record<string, string[]>>(() => {
        const restrictions: Record<string, string[]> = {};
        if (policies.editorVersionRestrictions) {
            Object.entries(policies.editorVersionRestrictions).forEach(([editorId, policy]) => {
                restrictions[editorId] = policy.allowedVersions || [];
            });
        }
        return restrictions;
    });

    const [isDirty, setIsDirty] = useState(false);

    const isInvalidSelection = useMemo(() => {
        return !Object.entries(selectedEditors).some(([_, checked]) => checked);
    }, [selectedEditors]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        const params: PartialMessage<OrganizationPolicies> = {
            allowedEditorIds: Object.entries(selectedEditors)
                .filter(([_, checked]) => checked)
                .map(([id]) => id),
        };
        if (defaultEditor !== "invalid") {
            params.defaultEditorId = defaultEditor;
        }

        // Build editor version restrictions
        // Note: Only save restrictions when versions are specified
        // If no restriction exists for an editor, all versions are allowed
        const restrictions: Record<string, EditorVersionPolicy> = {};
        Object.entries(editorVersionRestrictions).forEach(([editorId, versions]) => {
            if (versions.length > 0) {
                restrictions[editorId] = new EditorVersionPolicy({
                    allowedVersions: versions,
                });
            }
        });
        params.editorVersionRestrictions = restrictions;

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [selectedEditors, defaultEditor, editorVersionRestrictions, updateOrganizationPolicies, onClose, toast]);

    const handleEditorSelectionChange = useCallback(
        (editorId: string, checked: boolean) => {
            setSelectedEditors((prev) => ({ ...prev, [editorId]: checked }));
            setIsDirty(true);
            if (!checked && defaultEditor === editorId) {
                const newDefaultEditor = Object.entries(selectedEditors).find(
                    ([id, checked]) => checked && id !== editorId,
                )?.[0];
                setDefaultEditor(newDefaultEditor || "invalid");
            }
            if (checked && defaultEditor === "invalid") {
                setDefaultEditor(editorId);
            }
        },
        [defaultEditor, selectedEditors],
    );

    const handleDefaultEditorChange = useCallback((editorId: string) => {
        setDefaultEditor(editorId);
        setIsDirty(true);
    }, []);

    const handleVersionRestrictionsChange = useCallback((editorId: string, versions: string[]) => {
        setEditorVersionRestrictions((prev) => {
            return { ...prev, [editorId]: versions };
        });
        setIsDirty(true);
    }, []);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="manage-editors-modal-content"
                data-track-location={TrackLocations.ManageEditorsModal}
            >
                <DialogHeader>
                    <DialogTitle>Available editors</DialogTitle>
                    <DialogDescription>Manage the editors available to your members</DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    {editors.map((editor, index) => {
                        const availableVersions = editor.versions?.map((v) => v.version) || [];
                        const hasVersionSupport = availableVersions.length > 0;
                        const selectedVersions = editorVersionRestrictions[editor.id] || [];
                        const showVersionRestrictions = ideMultiVersionEnabled && !ideMultiVersionLoading;

                        return (
                            <div key={`${editor.id}-${index}`} className="space-y-2">
                                <div className="group flex h-[32px] flex-row items-center gap-4 rounded-md px-2 hover:bg-surface-muted">
                                    <Switch
                                        disabled={!editable}
                                        state={selectedEditors[editor.id] ? "checked" : "unchecked"}
                                        onToggle={(checked) => handleEditorSelectionChange(editor.id, checked)}
                                        id={`editor-toggle-${index}`}
                                    />
                                    <div className="flex grow flex-row gap-2">
                                        <EditorIcon editor={editor} size="base" />
                                        <span>{editor.name}</span>
                                    </div>
                                    <div>
                                        {defaultEditor === editor.id && (
                                            <Text className="text-content-secondary">default</Text>
                                        )}
                                        {defaultEditor !== editor.id && selectedEditors[editor.id] && (
                                            <Button
                                                disabled={!editable}
                                                variant="ghost"
                                                size="md"
                                                className="hidden border-0 bg-none p-0 text-content-secondary hover:bg-transparent group-hover:block"
                                                onClick={() => handleDefaultEditorChange(editor.id)}
                                                data-tracking-id="make-default-manage-editors-modal"
                                            >
                                                make default
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {selectedEditors[editor.id] && hasVersionSupport && showVersionRestrictions && (
                                    <div className="ml-12 space-y-2 rounded-md border border-border-subtle bg-surface-secondary p-3">
                                        <div className="flex flex-col gap-2">
                                            <Text className="text-sm font-medium text-content-primary">
                                                Allowed versions
                                            </Text>
                                            <Text className="text-xs text-content-secondary">
                                                Select which major versions members can use. If none selected, all
                                                versions are allowed.
                                            </Text>
                                            <MultiSelect
                                                placeholder="Select versions..."
                                                options={availableVersions}
                                                value={selectedVersions}
                                                onChange={(versions) =>
                                                    handleVersionRestrictionsChange(editor.id, versions)
                                                }
                                                disabled={!editable}
                                                data-testid={`version-multiselect-${editor.id}`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </DialogBody>

                <DialogFooter className="items-center">
                    {isInvalidSelection && (
                        <div className="grow">
                            <Text className="text-base text-content-red">You must have 1 available editor</Text>
                        </div>
                    )}
                    <DialogClose asChild>
                        <Button type="button" variant="secondary">
                            Close
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        loading={updateOrganizationPolicies.isPending}
                        disabled={!editable || !isDirty || isInvalidSelection}
                        data-testid="save-editors-button"
                        type="button"
                        variant="primary"
                        data-tracking-id="save-editors-manage-editors-modal"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
